import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { EyeIcon } from '@heroicons/react/24/outline';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import apiService from "../../services/ApiService";
import { apiRoutes } from "../../shared/apiRoutes";
import { Party } from "../../shared/interfaces";
import PartyAccountDetail from "./PartyAccountDetail";
import { formatDateOnly } from "../../utils/util";
import DialogBox from "../common/DialogBox";
import Button from "../common/Button";
import { useAuthorization } from "../../auth/useAuthorization";
import { Role } from "../../auth/roles";

// Simple Card wrapper component (matching Match Report)
const Card: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ 
  children, 
  className = '', 
  style 
}) => (
  <div 
    className={`rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border-none shadow-[0_8px_30px_rgb(0,0,0,0.12)] ${className}`}
    style={{ ...style, height: style?.height || '100%', maxHeight: style?.maxHeight || '100%' }}
  >
    {children}
  </div>
);

interface PartyItem {
  id: number;
  code: string;
  name: string;
  amount: number;
  isConfirmed?: boolean;
  hideInGeneral?: boolean;
}

interface Filter {
  code: string;
}

interface PartySummary {
  partyId: number;
  partyCode: string;
  partyName: string;
  totalCredits: number;
  totalDebits: number;
  finalAmount: number;
  type: "Credit" | "Debit";
  isConfirmed?: boolean;
  hideInGeneral?: boolean;
}

interface PartySummaryResponse {
  parties: PartySummary[];
  totalCredits: number;
  totalDebits: number;
  profitAndLoss: number;
  pnLType: "Profit" | "Loss";
}

interface ClearOpeningBalanceResponse {
  success: boolean;
  message: string;
  transactionsMarked?: number;
  clearedAt?: string;
}

const formatAmount = (value: number): string => {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value / 100));
};

const TrialBalance: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuthorization();
  const isOwner = hasRole(Role.OWNER);
  const [filter, setFilter] = useState<Filter>({ code: "" });
  const [partyCode, setPartyCode] = useState<string>(''); // Current typed value
  const [validatedPartyCode, setValidatedPartyCode] = useState<string>(''); // Original validated code
  const [partyValidation, setPartyValidation] = useState<{ isValid: boolean; error: string; partyName: string; partyId: number | null }>({
    isValid: false,
    error: '',
    partyName: '',
    partyId: null
  });
  const [parties, setParties] = useState<Party[]>([]);
  const [trialBalanceData, setTrialBalanceData] = useState<{ left: PartyItem[]; right: PartyItem[] }>({ left: [], right: [] });
  const [summaryData, setSummaryData] = useState<{ totalCredits: number; totalDebits: number; profitAndLoss: number }>({ totalCredits: 0, totalDebits: 0, profitAndLoss: 0 });
  const [tenantName, setTenantName] = useState<string>('P&L');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPartyForDetail, setSelectedPartyForDetail] = useState<{ id: number; code: string; name: string; amount: number } | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<{ id: number; side: 'left' | 'right' } | null>(null);
  const [showHiddenParties, setShowHiddenParties] = useState(false);
  const [showClearOpeningDialog, setShowClearOpeningDialog] = useState(false);
  const [clearPassword, setClearPassword] = useState('');
  const [clearPasswordError, setClearPasswordError] = useState('');
  const [clearingOpeningBalance, setClearingOpeningBalance] = useState(false);
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const partyInputRef = useRef<HTMLInputElement>(null);

  // Fetch parties for validation
  useEffect(() => {
    apiService.post<{ data: Party[] }>(apiRoutes.parties.lite, {
      page: 1,
      pageSize: 1000,
      orderBy: 'partyCode',
      orderDirection: 'asc'
    })
      .then(response => {
        const partiesData = response.data || [];
        setParties(partiesData);
      })
      .catch(error => {
        console.error('Error fetching parties:', error);
      });
  }, []);

  // Fetch tenant name
  useEffect(() => {
    const fetchTenantName = async () => {
      try {
        const profileResponse = await apiService.get('/Auth/profile');
        const profileData = (profileResponse as any).data || profileResponse;
        const tenant = profileData.tenantName || localStorage.getItem('tenantName') || 'P&L';
        setTenantName(tenant);
      } catch (err) {
        const tenant = localStorage.getItem('tenantName') || 'P&L';
        setTenantName(tenant);
      }
    };

    fetchTenantName();
  }, []);

  const fetchTrialBalance = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<PartySummaryResponse>(apiRoutes.transactions.partySummary, {
        timeout: 60000,
      });
      const data = response.data || response;

      const leftParties: PartyItem[] = [];
      const rightParties: PartyItem[] = [];

      if (data.parties && Array.isArray(data.parties)) {
        data.parties.forEach((party: PartySummary) => {
          const partyItem: PartyItem = {
            id: party.partyId,
            code: party.partyCode,
            name: party.partyName,
            amount: party.finalAmount,
            isConfirmed: party.isConfirmed === true,
            hideInGeneral: party.hideInGeneral ?? false,
          };

          if (party.finalAmount < 0) {
            leftParties.push(partyItem);
          } else if (party.finalAmount > 0) {
            rightParties.push(partyItem);
          }
        });
      }

      setTrialBalanceData({ left: leftParties, right: rightParties });
      setSummaryData({
        totalCredits: data.totalCredits || 0,
        totalDebits: data.totalDebits || 0,
        profitAndLoss: data.profitAndLoss || 0,
      });
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching trial balance:', err);
      setError(err?.message || 'Failed to load trial balance data');
      setLoading(false);
    }
  }, []);

  // Fetch trial balance data from API
  useEffect(() => {
    fetchTrialBalance();
  }, [fetchTrialBalance]);

  const closeClearOpeningDialog = useCallback(() => {
    if (clearingOpeningBalance) return;
    setShowClearOpeningDialog(false);
    setClearPassword('');
    setClearPasswordError('');
  }, [clearingOpeningBalance]);

  const handleClearOpeningBalance = useCallback(async () => {
    if (!clearPassword.trim()) {
      setClearPasswordError('Password is required.');
      return;
    }

    setClearingOpeningBalance(true);
    setClearPasswordError('');
    try {
      const response = await apiService.post<ClearOpeningBalanceResponse>(
        apiRoutes.transactions.clearOpeningBalance,
        { password: clearPassword },
      );
      const result = response.data ?? response;
      setShowClearOpeningDialog(false);
      setClearPassword('');
      setClearPasswordError('');
      await fetchTrialBalance();
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message:
              result.message ||
              'Opening balance cleared. Trial Balance will reflect only new transactions.',
            type: 'success',
          },
        }),
      );
    } catch (err: any) {
      const message =
        err?.message ||
        err?.error ||
        (typeof err === 'string' ? err : null) ||
        'Failed to clear opening balance.';
      setClearPasswordError(message);
    } finally {
      setClearingOpeningBalance(false);
    }
  }, [clearPassword, closeClearOpeningDialog, fetchTrialBalance]);

  // Validate party code format (same as CreateParty.tsx)
  const validatePartyCodeFormat = (code: string): { isValid: boolean; error: string } => {
    if (!code || code.trim().length === 0) {
      return { isValid: false, error: 'Party code is required' };
    }
    if (code.length < 1) {
      return { isValid: false, error: 'Party code must be 1 characters' };
    }
    if (code.length > 5) {
      return { isValid: false, error: 'Party code must be 5 characters' };
    }
    if (!/^[a-zA-Z0-9]+$/.test(code)) {
      return { isValid: false, error: 'Party code must be alphanumeric' };
    }
    return { isValid: true, error: '' };
  };

  // Validate and fetch party by code
  const validateAndFetchParty = async (code: string): Promise<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> => {
    // Extract code part if user pasted "CODE - NAME" format
    const codeMatch = code.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const trimmedCode = codeMatch ? codeMatch[1].toUpperCase().trim() : code.trim().toUpperCase();
    
    // First validate format
    const formatValidation = validatePartyCodeFormat(trimmedCode);
    if (!formatValidation.isValid) {
      const validationResult = {
        isValid: false,
        error: formatValidation.error,
        partyName: '',
        partyId: null
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode(''); // Clear validated code
      setFilter({ code: '' }); // Clear filter
      return validationResult;
    }

    // Check if party exists in local parties list first
    const filteredParties = (parties || []).filter(p => p.id !== 1);
    const foundParty = filteredParties.find(p => p.partyCode.toUpperCase() === trimmedCode);
    
    if (foundParty) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: foundParty.partyName,
        partyId: foundParty.id
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode(trimmedCode); // Store validated code
      setFilter({ code: trimmedCode }); // Update filter
      // Update input to show "CODE - NAME"
      if (partyInputRef.current) {
        partyInputRef.current.value = `${foundParty.partyCode} - ${foundParty.partyName}`;
      }
      setPartyCode(trimmedCode); // Update state with validated code
      return validationResult;
    }

    // If not found locally, try to fetch from API
    try {
      const partyData = await apiService.get(apiRoutes.parties.getByCode(trimmedCode));
      if (partyData && partyData.id && partyData.id !== 1) {
        const validationResult = {
          isValid: true,
          error: '',
          partyName: partyData.partyName || '',
          partyId: partyData.id
        };
        setPartyValidation(validationResult);
        setValidatedPartyCode(trimmedCode); // Store validated code
        setFilter({ code: trimmedCode }); // Update filter
        // Update input to show "CODE - NAME"
        if (partyInputRef.current) {
          partyInputRef.current.value = `${partyData.partyCode} - ${partyData.partyName}`;
        }
        setPartyCode(trimmedCode); // Update state with validated code
        return validationResult;
      } else {
        const validationResult = {
          isValid: false,
          error: 'Invalid party code',
          partyName: '',
          partyId: null
        };
        setPartyValidation(validationResult);
        setValidatedPartyCode(''); // Clear validated code
        setFilter({ code: '' }); // Clear filter
        return validationResult;
      }
    } catch (error) {
      const validationResult = {
        isValid: false,
        error: 'Invalid party code',
        partyName: '',
        partyId: null
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode(''); // Clear validated code
      setFilter({ code: '' }); // Clear filter
      return validationResult;
    }
  };

  // Handle party code input change
  const handlePartyCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Extract code part if user pasted "CODE - NAME" format, otherwise use the typed value
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    
    // Check if user is typing a new value (different from validated code)
    const isNewValue = codeOnly !== validatedPartyCode;
    
    if (isNewValue) {
      // User is typing a new value, clear validation and show only the code
      setPartyCode(codeOnly);
      setValidatedPartyCode(''); // Clear validated code
      setFilter({ code: codeOnly }); // Update filter with typed code
      setPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      // Update input to show only the code (remove "CODE - NAME" format if present)
      if (partyInputRef.current && value !== codeOnly) {
        partyInputRef.current.value = codeOnly;
      }
    } else {
      // User hasn't changed the validated code, keep the display format
      setPartyCode(codeOnly);
    }
  };

  // Handle party code blur
  const handlePartyCodeBlur = async () => {
    const value = partyInputRef.current?.value || partyCode;
    // Extract code part if value contains "CODE - NAME" format
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
    
    if (codeToValidate) {
      // Only validate if it's a new value (different from validated code)
      if (codeToValidate !== validatedPartyCode) {
        await validateAndFetchParty(codeToValidate);
      }
    } else {
      setPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      setValidatedPartyCode('');
      setFilter({ code: '' }); // Clear filter
      if (partyInputRef.current) {
        partyInputRef.current.value = '';
      }
    }
  };

  const filterData = useCallback(
    (data: PartyItem[]): PartyItem[] =>
      data.filter((item: PartyItem) => {
        if (!showHiddenParties && (item.hideInGeneral ?? false)) {
          return false;
        }
        return filter.code === '' || item.code.toLowerCase().includes(filter.code.toLowerCase());
      }),
    [filter.code, showHiddenParties],
  );

  const hiddenPartyCount = [...trialBalanceData.left, ...trialBalanceData.right].filter(
    (p) => p.hideInGeneral ?? false,
  ).length;

  const filteredLeft = useMemo(
    () => filterData(trialBalanceData.left),
    [trialBalanceData.left, filterData],
  );

  const filteredRight = useMemo(
    () => filterData(trialBalanceData.right),
    [trialBalanceData.right, filterData],
  );

  /** Totals derived from rows currently shown (respects party filter and hidden-party toggle). */
  const displayedSideTotals = useMemo(() => {
    const debitTotal = filteredLeft.reduce((sum, p) => sum + Math.abs(p.amount), 0);
    const creditTotal = filteredRight.reduce((sum, p) => sum + Math.abs(p.amount), 0);
    const pnlDiff = Math.abs(debitTotal - creditTotal);
    const debitHasPnl = debitTotal < creditTotal && pnlDiff !== 0;
    const creditHasPnl = creditTotal < debitTotal && pnlDiff !== 0;
    return {
      debitTotal,
      creditTotal,
      pnlDiff,
      debitHasPnl,
      creditHasPnl,
      debitFinalTotal: debitTotal + (debitHasPnl ? pnlDiff : 0),
      creditFinalTotal: creditTotal + (creditHasPnl ? pnlDiff : 0),
    };
  }, [filteredLeft, filteredRight]);

  const pageRef = useRef<HTMLDivElement>(null);

  // Fill the space under the site header so the document itself does not scroll.
  useEffect(() => {
    const fitToViewport = () => {
      const page = pageRef.current;
      if (!page) return;
      const top = Math.max(0, page.getBoundingClientRect().top);
      page.style.height = `${Math.max(0, window.innerHeight - top)}px`;
    };

    fitToViewport();
    window.addEventListener('resize', fitToViewport);
    return () => window.removeEventListener('resize', fitToViewport);
  }, []);

  const scrollRowIntoSide = (side: 'left' | 'right', rowElement: HTMLElement) => {
    const container = side === 'left' ? leftScrollRef.current : rightScrollRef.current;
    if (!container) return;
    const rowRect = rowElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    if (rowRect.top < containerRect.top) {
      container.scrollTop -= containerRect.top - rowRect.top;
    } else if (rowRect.bottom > containerRect.bottom) {
      container.scrollTop += rowRect.bottom - containerRect.bottom;
    }
  };

  const getProfitLossColor = (amount: number) => {
    if (amount < 0) return 'text-red-600 dark:text-red-400';
    if (amount > 0) return 'text-emerald-600 dark:text-emerald-400';
    return 'text-slate-900 dark:text-slate-100';
  };

  const getProfitLossSymbol = (amount: number) => {
    if (amount > 0) return '+';
    if (amount < 0) return '-';
    return '';
  };

  const handleDownloadReport = useCallback(() => {
    if (filteredLeft.length === 0 && filteredRight.length === 0 && displayedSideTotals.pnlDiff === 0) {
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'No data to download', type: 'error' }
      }));
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const now = new Date();
    const dateStr = formatDateOnly(now) || '';

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Trial Balance', pageWidth / 2, 14, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${tenantName}  |  Generated: ${dateStr}`, pageWidth / 2, 20, { align: 'center' });

    const tableTop = 26;

    const { pnlDiff, debitHasPnl, creditHasPnl, debitFinalTotal, creditFinalTotal } = displayedSideTotals;

    type SideRow = [string, string, string];
    type CombinedRow = [string, string, string, string, string, string];

    const buildSideRows = (
      parties: PartyItem[],
      includePnl: boolean,
      finalTotal: number,
    ): SideRow[] => {
      const rows: SideRow[] = parties.map((p, i) => [
        String(i + 1),
        `${p.code} - ${p.name}${(p.hideInGeneral ?? false) ? ' (Hidden)' : ''}`,
        formatAmount(p.amount),
      ]);
      if (includePnl) {
        rows.push([String(rows.length + 1), `P&L - ${tenantName}`, formatAmount(pnlDiff)]);
      }
      rows.push(['', 'TOTAL', formatAmount(finalTotal)]);
      return rows;
    };

    const debitSideRows = buildSideRows(filteredLeft, debitHasPnl, debitFinalTotal);
    const creditSideRows = buildSideRows(filteredRight, creditHasPnl, creditFinalTotal);
    const rowCount = Math.max(debitSideRows.length, creditSideRows.length);

    const emptySide: SideRow = ['', '', ''];
    const body: CombinedRow[] = Array.from({ length: rowCount }, (_, i) => {
      const debit = debitSideRows[i] ?? emptySide;
      const credit = creditSideRows[i] ?? emptySide;
      return [...debit, ...credit];
    });

    const usableWidth = pageWidth - margin * 2;
    const sideWidth = usableWidth / 2;
    const numColWidth = 8;
    const amtColWidth = 28;
    const nameColWidth = sideWidth - numColWidth - amtColWidth;

    autoTable(doc, {
      startY: tableTop,
      margin: { left: margin, right: margin },
      tableWidth: usableWidth,
      head: [['#', 'DEBIT', 'Amount', '#', 'CREDIT', 'Amount']],
      body,
      styles: { fontSize: 7.5, cellPadding: 1.5, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: numColWidth, halign: 'center' },
        1: { cellWidth: nameColWidth },
        2: { cellWidth: amtColWidth, halign: 'right' },
        3: { cellWidth: numColWidth, halign: 'center' },
        4: { cellWidth: nameColWidth },
        5: { cellWidth: amtColWidth, halign: 'right' },
      },
      didParseCell: (data: any) => {
        if (data.section === 'head') {
          if (data.column.index <= 2) {
            data.cell.styles.fillColor = [220, 38, 38];
            data.cell.styles.textColor = 255;
            data.cell.styles.fontStyle = 'bold';
          } else {
            data.cell.styles.fillColor = [16, 185, 129];
            data.cell.styles.textColor = 255;
            data.cell.styles.fontStyle = 'bold';
          }
          return;
        }

        const rowIndex = data.row.index;
        const isLastRow = rowIndex === rowCount - 1;
        const isDebitCol = data.column.index <= 2;

        if (isLastRow) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [241, 245, 249];
          return;
        }

        if (isDebitCol && debitHasPnl && rowIndex === debitSideRows.length - 2) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [236, 253, 245];
        } else if (!isDebitCol && creditHasPnl && rowIndex === creditSideRows.length - 2) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [254, 242, 242];
        }
      },
    });

    // Summary line at the bottom
    const finalY = (doc as any).lastAutoTable?.finalY || tableTop + 20;
    const summaryY = finalY + 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const pnlLabel = summaryData.profitAndLoss > 0 ? 'Profit' : summaryData.profitAndLoss < 0 ? 'Loss' : 'P&L';
    doc.text(
      `Total Debit: ${formatAmount(debitFinalTotal)}    |    Total Credit: ${formatAmount(creditFinalTotal)}    |    ${pnlLabel}: ${formatAmount(pnlDiff)}`,
      pageWidth / 2,
      summaryY,
      { align: 'center' }
    );

    doc.save(`Trial_Balance_${tenantName}_${now.toISOString().slice(0, 10)}.pdf`);

    window.dispatchEvent(new CustomEvent('show-toast', {
      detail: { message: 'Trial Balance report downloaded', type: 'success' }
    }));
  }, [filteredLeft, filteredRight, displayedSideTotals, summaryData.profitAndLoss, tenantName]);

  const openPartyDetail = (item: PartyItem) => {
    setSelectedPartyForDetail({
      id: item.id,
      code: item.code,
      name: item.name,
      amount: item.amount,
    });
    setIsDetailDialogOpen(true);
  };

  const openPartyLedger = (item: PartyItem) => {
    const params = new URLSearchParams({
      partyCode: item.code,
      partyId: String(item.id),
    });
    navigate(`/report/ledger?${params.toString()}`);
  };

  const handlePartyConfirmChange = async (partyId: number, isConfirmed: boolean) => {
    try {
      await apiService.post(apiRoutes.transactions.confirmParty, { partyId, isConfirmed });
      setTrialBalanceData((prev) => {
        const update = (list: PartyItem[]) =>
          list.map((p) => (p.id === partyId ? { ...p, isConfirmed } : p));
        return { left: update(prev.left), right: update(prev.right) };
      });
    } catch (err: any) {
      console.error('Error updating party confirmation:', err);
    }
  };

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isDetailDialogOpen || showClearOpeningDialog) return;
      const activeEl = document.activeElement;
      const checkboxFocused = activeEl instanceof HTMLInputElement && activeEl.type === 'checkbox';
      if (!checkboxFocused && (activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA' || activeEl?.tagName === 'SELECT')) {
        return;
      }

      // Get all navigable rows (including P&L row when present)
      const { pnlDiff, debitHasPnl, creditHasPnl } = displayedSideTotals;
      const allRows: Array<{ id: number; side: 'left' | 'right'; index: number; isPL?: boolean }> = [];
      
      filteredLeft.forEach((item, index) => {
        allRows.push({ id: item.id, side: 'left', index });
      });
      if (debitHasPnl) {
        allRows.push({ id: 0, side: 'left', index: filteredLeft.length, isPL: true });
      }
      filteredRight.forEach((item, index) => {
        allRows.push({ id: item.id, side: 'right', index });
      });
      if (creditHasPnl) {
        allRows.push({ id: 0, side: 'right', index: filteredRight.length, isPL: true });
      }

      if (allRows.length === 0) return;

      const focusRow = (row: { id: number; side: 'left' | 'right' }) => {
        setSelectedRowId({ id: row.id, side: row.side });
        const rowElement = rowRefs.current.get(`${row.side}-${row.id}`);
        if (rowElement) {
          scrollRowIntoSide(row.side, rowElement);
        }
      };

      let currentIndex = -1;
      if (selectedRowId) {
        currentIndex = allRows.findIndex(
          row => row.id === selectedRowId.id && row.side === selectedRowId.side
        );
      }

      if ((e.key === ' ' || e.code === 'Space') && !e.repeat && selectedRowId && selectedRowId.id !== 0) {
        const party = (selectedRowId.side === 'left' ? filteredLeft : filteredRight)
          .find((item) => item.id === selectedRowId.id);
        if (!party) return;
        e.preventDefault();
        void handlePartyConfirmChange(party.id, party.isConfirmed !== true);
        return;
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const targetSide = e.key === 'ArrowRight' ? 'right' : 'left';
        const targetRows = allRows.filter(row => row.side === targetSide);
        if (targetRows.length === 0) return;

        let targetIndex = 0;
        if (selectedRowId) {
          const currentSideRows = allRows.filter(row => row.side === selectedRowId.side);
          const indexOnSide = currentSideRows.findIndex(
            row => row.id === selectedRowId.id && row.side === selectedRowId.side
          );
          if (indexOnSide >= 0) {
            targetIndex = Math.min(indexOnSide, targetRows.length - 1);
          }
        }
        focusRow(targetRows[targetIndex]);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = currentIndex < allRows.length - 1 ? currentIndex + 1 : 0;
        focusRow(allRows[nextIndex]);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : allRows.length - 1;
        focusRow(allRows[prevIndex]);
      } else if (e.key === 'Enter' && selectedRowId) {
        e.preventDefault();
        const selectedRow = allRows.find(
          row => row.id === selectedRowId.id && row.side === selectedRowId.side
        );
        if (selectedRow) {
          if (selectedRow.isPL) {
            const pnlDisplayAmount = selectedRow.side === 'left' ? -Math.abs(pnlDiff) : Math.abs(pnlDiff);
            openPartyLedger({ id: 0, code: 'P&L', name: `P&L - ${tenantName}`, amount: pnlDisplayAmount });
          } else {
            const partyData = selectedRow.side === 'left' 
              ? filteredLeft[selectedRow.index]
              : filteredRight[selectedRow.index];
            if (partyData) {
              openPartyLedger(partyData);
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedRowId, filteredLeft, filteredRight, displayedSideTotals, tenantName, navigate, isDetailDialogOpen, showClearOpeningDialog]);

  const renderTable = (
    parties: PartyItem[],
    isLeft: boolean,
    scrollRef: React.RefObject<HTMLDivElement | null>,
  ) => {
    const filteredParties = isLeft ? filteredLeft : filteredRight;
    const hasPnl = isLeft ? displayedSideTotals.debitHasPnl : displayedSideTotals.creditHasPnl;
    const finalTotal = isLeft ? displayedSideTotals.debitFinalTotal : displayedSideTotals.creditFinalTotal;
    const pnlValue = displayedSideTotals.pnlDiff;
    
    // P&L display amount: negative for debit side, positive for credit side
    const pnlDisplayAmount = hasPnl ? (isLeft ? -Math.abs(pnlValue) : Math.abs(pnlValue)) : 0;
    
    const isDebit = isLeft;
    const side = isLeft ? 'left' : 'right';

    return (
      <Card className="overflow-hidden h-full min-h-0 flex flex-col" style={{ height: '100%', maxHeight: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header with Icon */}
        <div className={`py-1.5 px-3 flex-shrink-0 ${isDebit ? 'bg-gradient-to-r from-red-500 via-rose-500 to-red-600' : 'bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600'}`} style={{ flexShrink: 0 }}>
          <div className="flex items-center justify-center gap-1.5">
            {isDebit ? (
              <svg className="w-4 h-4 text-red-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-emerald-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            )}
            <h2 className={`text-xs font-bold uppercase tracking-wider ${isDebit ? 'text-red-50' : 'text-emerald-50'}`}>
              {isDebit ? 'Debit' : 'Credit'}
            </h2>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden" style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
          {/* Table Header - Fixed */}
          <div className="flex-shrink-0 bg-slate-100/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700" style={{ flexShrink: 0 }}>
            <div className="grid grid-cols-[1fr,auto,auto,auto] sm:grid-cols-[2fr,1fr,auto,auto] px-2 sm:px-4 py-1.5">
              <div className="text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                Name
              </div>
              <div className="text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide text-right">
                Amount
              </div>
              <div className="text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide text-center w-7 sm:w-8">
              </div>
              <div className="text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide text-center w-7 sm:w-8">
              </div>
            </div>
          </div>

          {/* Table Body - Scrollable */}
          <div 
            ref={scrollRef} 
            className="divide-y divide-slate-200 dark:divide-slate-700 flex-1 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-slate-400 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent"
            style={{ 
              flex: '1 1 0', 
              minHeight: 0, 
              overflowY: 'auto', 
              overflowX: 'hidden', 
              overscrollBehavior: 'contain',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgb(148 163 184) transparent'
            }}
          >
            {filteredParties.length === 0 && !hasPnl ? (
              <div className="text-center p-8 italic text-sm text-slate-600 dark:text-slate-400">No data available</div>
            ) : (
              <>
                {filteredParties.map((item, index) => {
                  const rowKey = `${side}-${item.id}`;
                  const isSelected = selectedRowId?.id === item.id && selectedRowId?.side === side;
                  return (
                    <div
                      key={item.id}
                      ref={(el) => {
                        if (el) {
                          rowRefs.current.set(rowKey, el);
                        } else {
                          rowRefs.current.delete(rowKey);
                        }
                      }}
                      onClick={() => openPartyLedger(item)}
                      title="Open ledger"
                      className={`grid grid-cols-[1fr,auto,auto,auto] sm:grid-cols-[2fr,1fr,auto,auto] px-2 sm:px-4 py-1.5 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 hover:shadow-sm cursor-pointer transition-all duration-200 rounded-sm ${
                        isSelected ? 'bg-blue-200/70 dark:bg-blue-900/50 ring-2 ring-blue-500' : ''
                      } ${(item.hideInGeneral ?? false) ? 'opacity-60' : ''}`}
                    >
                      <div className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100 uppercase tracking-wide truncate">
                        {item.code} - {item.name}
                        {(item.hideInGeneral ?? false) && (
                          <span className="ml-1.5 inline-flex rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            Hidden
                          </span>
                        )}
                      </div>
                      <div className={`text-xs sm:text-sm font-bold text-right whitespace-nowrap pl-1 ${getProfitLossColor(item.amount)}`}>
                        {getProfitLossSymbol(item.amount)}{formatAmount(item.amount)}
                      </div>
                      <div className="text-center w-7 sm:w-8 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                          checked={item.isConfirmed === true}
                          onChange={(e) => handlePartyConfirmChange(item.id, e.target.checked)}
                        />
                      </div>
                      <div className="text-center w-7 sm:w-8 flex items-center justify-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openPartyDetail(item);
                          }}
                          className="p-0.5 sm:p-1 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                          title="View Party Details"
                        >
                          <EyeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {/* Add P&L row if this side has less total */}
                {hasPnl && (
                  <div 
                    ref={(el) => {
                      if (el) rowRefs.current.set(`${side}-0`, el);
                      else rowRefs.current.delete(`${side}-0`);
                    }}
                    onClick={() => openPartyLedger({ id: 0, code: 'P&L', name: `P&L - ${tenantName}`, amount: pnlDisplayAmount })}
                    title="Open ledger"
                    className={`grid grid-cols-[1fr,auto,auto,auto] sm:grid-cols-[2fr,1fr,auto,auto] px-2 sm:px-4 py-1.5 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 hover:shadow-sm cursor-pointer transition-all duration-200 rounded-sm border-l-4 ${
                      isDebit 
                        ? 'bg-emerald-100/70 dark:bg-emerald-900/40 border-emerald-600' 
                        : 'bg-red-100/70 dark:bg-red-900/40 border-red-600'
                    } ${selectedRowId?.id === 0 && selectedRowId?.side === side ? 'ring-2 ring-blue-500' : ''}`}
                  >
                    <div className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wide truncate">
                      P&L - {tenantName}
                    </div>
                    <div className={`text-xs sm:text-sm font-bold text-right whitespace-nowrap pl-1 ${getProfitLossColor(pnlDisplayAmount)}`}>
                      {getProfitLossSymbol(pnlDisplayAmount)}{formatAmount(Math.abs(pnlValue))}
                    </div>
                    <div className="text-center w-7 sm:w-8 flex items-center justify-center">
                      <input type="checkbox" className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500" disabled />
                    </div>
                    <div className="text-center w-7 sm:w-8 flex items-center justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openPartyDetail({ id: 0, code: 'P&L', name: `P&L - ${tenantName}`, amount: pnlDisplayAmount });
                        }}
                        className="p-0.5 sm:p-1 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                        title="View P&L Details"
                      >
                        <EyeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Table Footer - Fixed at bottom */}
          <div className="flex-shrink-0 bg-slate-200/90 dark:bg-slate-700/90 border-t-2 border-slate-200 dark:border-slate-700 rounded-b-lg" style={{ flexShrink: 0 }}>
            <div className="grid grid-cols-[1fr,auto,auto,auto] sm:grid-cols-[2fr,1fr,auto,auto] px-2 sm:px-4 py-2">
              <div className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wide">
                Total
              </div>
              <div className={`text-sm sm:text-base font-bold text-right whitespace-nowrap pl-1 ${getProfitLossColor(isLeft ? -finalTotal : finalTotal)}`}>
                {getProfitLossSymbol(isLeft ? -finalTotal : finalTotal)}{formatAmount(finalTotal)}
              </div>
              <div className="w-7 sm:w-8"></div>
              <div className="w-7 sm:w-8"></div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div ref={pageRef} className="w-full bg-white dark:bg-slate-900 flex flex-col overflow-hidden" style={{ height: 'calc(100dvh - 40px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header Card */}
      <div className="flex-shrink-0 px-2 pb-1 pt-1 md:px-4" style={{ flexShrink: 0 }}>
        <Card className="overflow-hidden border-none">
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 px-2 py-0.5 sm:px-3 dark:from-slate-800 dark:via-slate-700 dark:to-slate-800">
            <h1 className="text-xs font-bold text-white text-center tracking-tight sm:text-sm">
              Trial Balance
            </h1>
          </div>
        </Card>
      </div>

      {/* Filter Section */}
      <div className="flex-shrink-0 px-2 pb-1 pt-0 md:px-4" style={{ flexShrink: 0 }}>
        <Card className="overflow-hidden border-none">
          <div className="bg-slate-50 px-2 py-1.5 dark:bg-slate-800 sm:px-2.5 sm:py-2">
            <div className="flex flex-col items-stretch justify-between gap-1.5 sm:flex-row sm:items-center sm:gap-3">
              {/* Filter Fields */}
              <div className="flex flex-grow gap-2">
                <div className="min-w-0 max-w-full flex-1 sm:max-w-[220px]">
                  <input
                    ref={partyInputRef}
                    type="text"
                    onChange={handlePartyCodeChange}
                    onBlur={handlePartyCodeBlur}
                    maxLength={50}
                    placeholder="Party Code"
                    className={`h-7 w-full rounded border px-2 text-xs leading-tight bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${partyValidation.error ? 'border-red-600' : partyValidation.isValid ? 'border-green-600' : 'border-slate-300 dark:border-slate-600'}`}
                  />
                  {partyValidation.error && (
                    <div className="mt-0.5 text-[11px] text-red-600">{partyValidation.error}</div>
                  )}
                </div>
              </div>

              {hiddenPartyCount > 0 && (
                <label className="flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded border border-slate-300 bg-white px-2 text-xs text-slate-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={showHiddenParties}
                    onChange={(e) => setShowHiddenParties(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  SH ({hiddenPartyCount})
                </label>
              )}

              {isOwner && (
                <button
                  type="button"
                  onClick={() => setShowClearOpeningDialog(true)}
                  disabled={loading || clearingOpeningBalance}
                  className="flex h-7 shrink-0 items-center justify-center rounded border border-amber-600 bg-amber-50 px-2.5 text-xs font-medium text-amber-800 shadow-sm transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-500 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60"
                  title="Exclude current transactions from Trial Balance (data is not deleted)"
                >
                  Clear Opening Balance
                </button>
              )}

              {/* Download Button */}
              <button
                onClick={handleDownloadReport}
                disabled={loading || (trialBalanceData.left.length === 0 && trialBalanceData.right.length === 0)}
                className="flex h-7 shrink-0 items-center justify-center gap-1 rounded bg-blue-600 px-2.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" />
                Download
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Tables Section */}
      <div className="flex-1 min-h-0 overflow-hidden px-2 md:px-4 pb-2 pt-0.5" style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-300 border-t-transparent"></div>
            <span className="ml-3 text-slate-900 dark:text-slate-100 text-base font-medium">Loading trial balance...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-900/80 border-2 border-red-500 text-red-100 px-4 py-3 rounded-lg mb-4 text-sm shadow-lg">
            <span className="font-semibold">Error: </span>{error}
          </div>
        )}

        {!loading && !error && (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] md:grid-rows-[minmax(0,1fr)] gap-2 md:gap-4 min-h-0 overflow-hidden">
            {/* Debit Table */}
            <div className="h-full min-h-0 overflow-hidden">
              {renderTable(
                trialBalanceData.left, 
                true, 
                leftScrollRef,
              )}
            </div>

            {/* Credit Table */}
            <div className="h-full min-h-0 overflow-hidden">
              {renderTable(
                trialBalanceData.right, 
                false, 
                rightScrollRef,
              )}
            </div>
          </div>
        )}

        {!loading && !error && trialBalanceData.left.length === 0 && trialBalanceData.right.length === 0 && (
          <div className="text-slate-600 dark:text-slate-400 text-center py-8">
            No trial balance data available
          </div>
        )}
      </div>

      {/* Party Account Detail Dialog */}
      {selectedPartyForDetail && (
        <PartyAccountDetail
          isOpen={isDetailDialogOpen}
          onClose={() => {
            setIsDetailDialogOpen(false);
            setSelectedPartyForDetail(null);
          }}
          partyId={selectedPartyForDetail.id}
          partyCode={selectedPartyForDetail.code}
          partyName={selectedPartyForDetail.name}
          amount={selectedPartyForDetail.amount}
          onTrialBalanceConfirmationChange={(isConfirmed) => {
            const partyId = selectedPartyForDetail.id;
            setTrialBalanceData((prev) => {
              const update = (list: PartyItem[]) =>
                list.map((p) => (p.id === partyId ? { ...p, isConfirmed } : p));
              return { left: update(prev.left), right: update(prev.right) };
            });
          }}
        />
      )}

      <DialogBox
        isOpen={showClearOpeningDialog}
        onClose={closeClearOpeningDialog}
        title="Clear Opening Balance"
        panelClassName="w-[min(100vw-2rem,28rem)]"
      >
        <div className="p-4 space-y-3">
          <p className="text-sm text-slate-700 dark:text-slate-300">
            This excludes all current transactions from Trial Balance only. No data is deleted from the database.
            After clearing, only new game, match, and havala activity will appear on Trial Balance.
          </p>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            Enter your password to confirm.
          </p>
          <div>
            <label htmlFor="clear-opening-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Password
            </label>
            <input
              id="clear-opening-password"
              type="password"
              value={clearPassword}
              onChange={(e) => {
                setClearPassword(e.target.value);
                setClearPasswordError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleClearOpeningBalance();
                }
              }}
              autoFocus
              autoComplete="current-password"
              disabled={clearingOpeningBalance}
              className="w-full h-[34px] px-2 py-1 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your login password"
            />
            {clearPasswordError ? (
              <p className="text-xs text-red-600 mt-1">{clearPasswordError}</p>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              onClick={closeClearOpeningDialog}
              disabled={clearingOpeningBalance}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleClearOpeningBalance()}
              isProcessing={clearingOpeningBalance}
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md"
            >
              Clear Opening Balance
            </Button>
          </div>
        </div>
      </DialogBox>
    </div>
  );
};

export default TrialBalance;